import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  ChatHintContext,
  CHAT_HINT_BLOCKED_RETRY_MS,
  CHAT_HINT_INITIAL_DELAY_MS,
  CHAT_HINT_REPEAT_DELAY_MS,
  CHAT_HINT_VISIBLE_MS,
  MAX_CHAT_HINTS_PER_SESSION,
  MAX_CHAT_HINT_BLOCKED_RETRIES,
  getChatHintContext,
  getChatHintMessage,
} from '../constants/chatHints';
import {
  CHAT_HINTS_DISABLED_KEY,
  advanceChatHintRotation,
  areChatHintsEnabled,
  readChatHintRotation,
  readChatHintSession,
  setChatHintsEnabled,
  writeChatHintSession,
} from '../services/chatHintStorage';

/**
 * Presence of a `role="dialog"` node says nothing about whether anything is
 * on screen: Chakra leaves every closed `Popover` mounted at
 * `visibility: hidden; opacity: 0`, and those still report layout boxes, so
 * `getClientRects()` and a bare `checkVisibility()` both call them visible.
 * A page with any popover in its chrome therefore looked permanently
 * blocked, which silenced hints outright.
 *
 * `checkVisibility` is the only thing that sees through this, and only when
 * asked about the two properties Chakra actually uses. Those flags were
 * renamed mid-standardisation, and Chromium 105-117 honours only the older
 * `checkVisibilityCSS`/`checkOpacity` spelling, so pass both: unrecognised
 * dictionary members are ignored, and being wrong here fails silently --
 * every hidden popover reads as visible and hints stop appearing at all.
 *
 * jsdom does not implement it, so the computed-style fallback is what the
 * tests exercise.
 */
const isOverlayVisible = (element: Element): boolean => {
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility({
      visibilityProperty: true,
      opacityProperty: true,
      checkVisibilityCSS: true,
      checkOpacity: true,
    });
  }
  const { display, visibility, opacity } = window.getComputedStyle(element);
  return display !== 'none' && visibility !== 'hidden' && opacity !== '0';
};

/**
 * True while a modal or drawer owns the screen. Users routinely land straight
 * in the new-quote modal from CRM, and a hint competing with it is precisely
 * the intrusion this feature must avoid. Read from the DOM rather than app
 * state because every overlay in the app -- Chakra modals, drawers, and the
 * chat panel itself -- is already marked with `role="dialog"`, so there is no
 * registry to keep in sync.
 */
const isOverlayOpen = (): boolean =>
  Array.from(document.querySelectorAll('[role="dialog"]')).some(
    isOverlayVisible,
  );

/**
 * Route-derived hint context. Split out from {@link useChatHint} so the chat
 * drawer can take the suggestions without dragging in the scheduler it has
 * no use for.
 */
export const useChatHintContext = (): ChatHintContext => {
  const { pathname } = useLocation();
  return useMemo(() => getChatHintContext(pathname), [pathname]);
};

export interface UseChatHintPreference {
  hintsEnabled: boolean;
  /** Turn hints off everywhere -- not per tenant, not per session. */
  disableHints: () => void;
  enableHints: () => void;
}

/**
 * The preference has two live readers with very different lifetimes: the
 * launcher, mounted for the whole app session, and the drawer, which
 * remounts every time it opens. Per-hook `useState` left the launcher stale
 * whenever the drawer changed the setting -- so re-enabling hints from the
 * drawer did nothing until a reload, breaking the one path back.
 *
 * `localStorage` is the store and these listeners are its change feed;
 * `areChatHintsEnabled` is a valid `getSnapshot` because it returns a
 * boolean, which React compares by value.
 *
 * The feed has two sources. The listener set carries changes made in this
 * tab, which `storage` deliberately does not fire for. The `storage` event
 * carries changes from the user's other tabs -- the opt-out claims to be
 * global, so switching it off in one tab has to stop hints in the rest
 * rather than leaving them running until each is reloaded.
 */
const hintPreferenceListeners = new Set<() => void>();

const subscribeToHintPreference = (onChange: () => void) => {
  hintPreferenceListeners.add(onChange);

  // `key` is null when another tab calls localStorage.clear() -- on logout,
  // say -- which resets the preference just as much as a targeted write.
  const handleStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === CHAT_HINTS_DISABLED_KEY) {
      onChange();
    }
  };
  window.addEventListener('storage', handleStorage);

  return () => {
    hintPreferenceListeners.delete(onChange);
    window.removeEventListener('storage', handleStorage);
  };
};

/** The durable on/off preference, readable and settable from either surface. */
export const useChatHintPreference = (): UseChatHintPreference => {
  const hintsEnabled = useSyncExternalStore(
    subscribeToHintPreference,
    areChatHintsEnabled,
  );

  const setPreference = useCallback((enabled: boolean) => {
    setChatHintsEnabled(enabled);
    hintPreferenceListeners.forEach((notify) => notify());
  }, []);

  const disableHints = useCallback(() => setPreference(false), [setPreference]);
  const enableHints = useCallback(() => setPreference(true), [setPreference]);

  return { hintsEnabled, disableHints, enableHints };
};

export interface UseChatHintOptions {
  /** Hints are only scheduled once the launcher is actually on screen. */
  enabled: boolean;
  userId: string | undefined;
  /** An open chat panel makes the hint redundant. */
  isChatPanelOpen: boolean;
}

export interface UseChatHint {
  message: string | null;
  suggestions: string[];
  hintsEnabled: boolean;
  /** Close this hint and stand down for the rest of the session. */
  dismissHint: () => void;
  /** Close it and never schedule another, on any device state we control. */
  disableHints: () => void;
  enableHints: () => void;
  /** Marks the session spent without counting as an explicit dismissal. */
  hintShownByOpeningChat: () => void;
}

/**
 * Drives the launcher hint: when it appears, what it says, and when it
 * gives up. Suggestions for the chat drawer come from the same route
 * context, so the bubble and the drawer always agree on where the user is.
 */
export const useChatHint = ({
  enabled,
  userId,
  isChatPanelOpen,
}: UseChatHintOptions): UseChatHint => {
  const [message, setMessage] = useState<string | null>(null);
  const context = useChatHintContext();
  const {
    hintsEnabled,
    disableHints: disablePreference,
    enableHints,
  } = useChatHintPreference();

  /**
   * The context is read at fire time, not captured when the timer is set, so
   * a hint that lands after the user has navigated speaks about where they
   * are now. Keeping it in a ref also stops navigation from restarting the
   * timers on every route change.
   */
  const contextRef = useRef(context);
  contextRef.current = context;

  const endSession = useCallback(
    (dismissed: boolean) => {
      setMessage(null);
      if (!userId) {
        return;
      }
      const current = readChatHintSession(userId);
      writeChatHintSession(userId, {
        shownCount: Math.max(current.shownCount, MAX_CHAT_HINTS_PER_SESSION),
        dismissed,
      });
    },
    [userId],
  );

  const dismissHint = useCallback(() => endSession(true), [endSession]);

  const hintShownByOpeningChat = useCallback(
    () => endSession(false),
    [endSession],
  );

  const disableHints = useCallback(() => {
    disablePreference();
    setMessage(null);
  }, [disablePreference]);

  // Scheduling. One effect owns the whole lifecycle -- show, auto-hide,
  // maybe repeat -- because splitting it across effects made the "only
  // repeat when the last one timed out" rule impossible to express.
  useEffect(() => {
    if (!enabled || !hintsEnabled || !userId || isChatPanelOpen) {
      // Clearing here covers the paths that turn hints off without going
      // through `disableHints` -- an opt-out from another tab, above all.
      // The cleanup below has already dropped the auto-hide timer, so a
      // bubble left standing at this point would never come down on its own.
      setMessage(null);
      return;
    }
    const stored = readChatHintSession(userId);
    if (stored.dismissed || stored.shownCount >= MAX_CHAT_HINTS_PER_SESSION) {
      // Same reasoning as above: reaching here on a `userId` change means the
      // previous user's bubble is on screen with its auto-hide timer already
      // cancelled, and the new user's session has no budget to take it down.
      setMessage(null);
      return;
    }

    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    let blockedRetries = 0;
    const show = () => {
      // Re-read rather than trusting `stored`: another tab, or an earlier
      // pass of this effect, may have spent the budget while we waited.
      const session = readChatHintSession(userId);
      if (
        session.dismissed ||
        session.shownCount >= MAX_CHAT_HINTS_PER_SESSION
      ) {
        return;
      }
      if (isOverlayOpen()) {
        blockedRetries += 1;
        if (blockedRetries <= MAX_CHAT_HINT_BLOCKED_RETRIES) {
          schedule(show, CHAT_HINT_BLOCKED_RETRY_MS);
        }
        return;
      }

      // Rotation is durable while the budget is per-session, so successive
      // hints differ both within a sitting and across days.
      const rotation = readChatHintRotation();
      setMessage(getChatHintMessage(contextRef.current, rotation));
      advanceChatHintRotation(rotation);

      const shownCount = session.shownCount + 1;
      writeChatHintSession(userId, { ...session, shownCount });

      schedule(() => {
        // Timing out is not a dismissal, so the budget alone decides
        // whether the user hears from Monty again this session.
        setMessage(null);
        if (shownCount < MAX_CHAT_HINTS_PER_SESSION) {
          schedule(show, CHAT_HINT_REPEAT_DELAY_MS);
        }
      }, CHAT_HINT_VISIBLE_MS);
    };

    schedule(show, CHAT_HINT_INITIAL_DELAY_MS);
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [enabled, hintsEnabled, userId, isChatPanelOpen]);

  return {
    message,
    suggestions: context.suggestions,
    hintsEnabled,
    dismissHint,
    disableHints,
    enableHints,
    hintShownByOpeningChat,
  };
};
